import { clientSchema } from "@/components/Forms/schema/caseFormSchemaInput.ts";
import { dtMetaSchema } from "@/components/Forms/schema/dtMetaSchema.ts";
import { mrtCaseSchema } from "@/components/Forms/schema/mrtCaseSchema.ts";
import { personSchema } from "@/components/Forms/schema/personSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "case",
    attributesSchema: mrtCaseSchema,
    documentMetaSchema: dtMetaSchema,
    relationships: {
        idClient: {
            relationshipType: "one_nullable",
            include: {
                type: "client",
                attributesSchema: clientSchema,
            },
        },
        idPeople: {
            relationshipType: "one_nullable",
            include: {
                type: "person",
                attributesSchema: personSchema,
            },
            required: false,
        },
    },
});

export const casesSelector = createDataSelector(resourceSelector);
export type LastTouchReportCases = ReturnType<typeof casesSelector>;

export type LastTouchReportCasesDocument = ReturnType<typeof resourceSelector>;
export type MrtLastTouchReportCase = LastTouchReportCasesDocument["data"][number];

type Props = {
    isQueryEnabled: boolean;
    reportType: "attorney" | "cm" | "all";
    columnFilters: MRT_ColumnFiltersState;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchReportLastTouch = ({
    isQueryEnabled = true,
    reportType,
    columnFilters,
    pagination,
    sorting,
}: Props): UseQueryResult<LastTouchReportCasesDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "report-last-touch",
            reportType,
            columnFilters,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const requestBody = {
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: columnFilters ?? [],
                sorting: sorting ?? [],
                reportType: reportType,
            };

            const response = await fetch(apiUrl("/report/last-touch"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData?.errors?.[0]?.title ??
                        `Request failed with status ${response.status}`,
                );
            }

            return await response.json();
        },
        select: resourceSelector,
    });
};

/** Fetches all records for export (bypasses pagination) */
export const fetchAllLastTouchRecords = async (
    fetch: ReturnType<typeof useAuthenticatedFetch>,
    reportType: "attorney" | "cm" | "all",
    columnFilters: MRT_ColumnFiltersState,
): Promise<LastTouchReportCasesDocument> => {
    const allRecords: LastTouchReportCasesDocument["data"] = [];
    let start = 0;
    const size = 250; // Max page size
    let totalCount = 0;

    // Fetch all pages
    do {
        const requestBody = {
            start,
            size,
            filters: columnFilters ?? [],
            sorting: [],
            reportType,
        };

        const response = await fetch(apiUrl("/report/last-touch"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData?.errors?.[0]?.title ?? `Request failed with status ${response.status}`,
            );
        }

        const data = resourceSelector(await response.json());
        allRecords.push(...data.data);
        totalCount = data.meta?.totalRowCount ?? 0;
        start += size;
    } while (start < totalCount);

    return {
        data: allRecords,
        meta: { totalRowCount: totalCount },
    } as LastTouchReportCasesDocument;
};
